import { motion, AnimatePresence } from 'framer-motion'
import { Room } from '../types'
import type { SendFn } from '../App'
import ItemReactions from './ItemReactions'

interface RevealProps {
  room: Room
  playerId: string
  send: SendFn
}

export default function Reveal({ room, playerId, send }: RevealProps) {
  const isRanker = playerId === room.currentRankerId
  const rankerName = room.currentRankerId ? room.players[room.currentRankerId]?.name : '???'
  const total = room.rankerRanking.length
  const showLineReveal = room.phase === 'lineReveal'

  const revealedItems = []
  for (let i = 0; i < room.revealedCount; i++) {
    const revealIndex = total - 1 - i
    if (revealIndex >= 0) {
      revealedItems.push({
        item: room.rankerRanking[revealIndex],
        rankPosition: revealIndex + 1,
        isAboveLine: revealIndex < room.rankerLineIndex,
      })
    }
  }

  const allRevealed = room.revealedCount >= total

  const revealNext = () => {
    if (room.revealedCount < total) {
      send('reveal_next', {})
    } else if (!room.lineRevealed) {
      send('reveal_line', {})
    }
  }

  const showResults = () => {
    send('next_round', {})
  }

  const getRankLabel = (position: number) => {
    if (position === 1) return '🥇'
    if (position === 2) return '🥈'
    if (position === 3) return '🥉'
    return `#${position}`
  }

  return (
    <div className="flex-1 flex flex-col items-center px-6 py-8 max-w-lg mx-auto w-full">
      <div className="text-center mb-8">
        <p className="font-body text-warmblack/50 text-base mb-1">
          {rankerName}'s actual preferences — Round {room.roundNumber}
        </p>
        <h2 className="font-display text-3xl font-bold text-warmblack">
          {allRevealed && !showLineReveal
            ? 'Where did the line go?'
            : showLineReveal
            ? 'The Line!'
            : `Reveal #${room.revealedCount + 1} of ${total}`}
        </h2>
      </div>

      <div className="flex-1 w-full flex flex-col gap-3">
        {showLineReveal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center mb-1">
            <span className="font-display text-sm font-bold text-sage uppercase tracking-widest">Positive</span>
          </motion.div>
        )}

        <AnimatePresence mode="popLayout">
          {[...revealedItems]
            .sort((a, b) => a.rankPosition - b.rankPosition)
            .map((revealed) => {
              const showLineAfter = showLineReveal && revealed.rankPosition === room.rankerLineIndex
              return (
                <div key={revealed.item}>
                  {showLineReveal ? (
                    <ItemReactions itemText={revealed.item} room={room} playerId={playerId} send={send}>
                      <motion.div
                        initial={{ scale: 0, opacity: 0, y: 50 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        transition={{ type: 'spring', damping: 12, stiffness: 200 }}
                        className={`flex items-center gap-3 px-5 py-5 rounded-xl border-2 ${
                          revealed.isAboveLine
                            ? 'bg-sage/10 border-sage/30'
                            : 'bg-coral/10 border-coral/30'
                        } shadow-sm`}
                      >
                        <span className="text-3xl w-12 text-center">{getRankLabel(revealed.rankPosition)}</span>
                        <span className="font-body text-xl font-bold flex-1 text-warmblack">{revealed.item}</span>
                      </motion.div>
                    </ItemReactions>
                  ) : (
                    <motion.div
                      initial={{ scale: 0, opacity: 0, y: 50 }}
                      animate={{ scale: 1, opacity: 1, y: 0 }}
                      transition={{ type: 'spring', damping: 12, stiffness: 200 }}
                      className="flex items-center gap-3 px-5 py-5 rounded-xl border-2 bg-white border-warmblack/10 shadow-sm"
                    >
                      <span className="text-3xl w-12 text-center">{getRankLabel(revealed.rankPosition)}</span>
                      <span className="font-body text-xl font-bold flex-1 text-warmblack">{revealed.item}</span>
                    </motion.div>
                  )}
                  {showLineAfter && (
                    <motion.div
                      initial={{ scaleX: 0, opacity: 0 }}
                      animate={{ scaleX: 1, opacity: 1 }}
                      transition={{ type: 'spring', damping: 8 }}
                      className="flex items-center gap-3 py-3"
                    >
                      <div className="flex-1 h-1.5 bg-gradient-to-r from-coral via-blush to-coral rounded-full" />
                      <span className="font-display text-base font-bold text-coral uppercase tracking-widest">The Line</span>
                      <div className="flex-1 h-1.5 bg-gradient-to-r from-coral via-blush to-coral rounded-full" />
                    </motion.div>
                  )}
                </div>
              )
            })}
        </AnimatePresence>

        {showLineReveal && room.rankerLineIndex < total && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="text-center mt-1">
            <span className="font-display text-sm font-bold text-coral/60 uppercase tracking-widest">Negative</span>
          </motion.div>
        )}

        {!allRevealed &&
          Array.from({ length: total - room.revealedCount }, (_, i) => (
            <motion.div
              key={`unrevealed-${i}`}
              className="flex items-center justify-center px-5 py-5 rounded-xl border-2 border-dashed border-warmblack/10 bg-warmblack/[0.02]"
            >
              <span className="font-body text-warmblack/20 text-xl">???</span>
            </motion.div>
          ))}
      </div>

      <div className="mt-8 w-full">
        {isRanker && !showLineReveal && (
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={revealNext}
            className="w-full bg-coral hover:bg-coral/90 text-white font-display font-bold text-2xl py-5 rounded-2xl shadow-lg transition-all active:scale-95"
          >
            {room.revealedCount === 0 ? 'Reveal First Item' : allRevealed ? 'Reveal the Line' : 'Reveal Next'}
          </motion.button>
        )}

        {showLineReveal && (
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', delay: 0.5 }}
            whileTap={{ scale: 0.95 }}
            onClick={showResults}
            className="w-full bg-sunflower hover:bg-sunflower/80 text-warmblack font-display font-bold text-2xl py-5 rounded-2xl shadow-lg transition-all"
          >
            See How Everyone Did
          </motion.button>
        )}

        {!isRanker && !showLineReveal && (
          <p className="text-center text-warmblack/40 font-body text-base">
            {rankerName} controls the reveal
          </p>
        )}
      </div>
    </div>
  )
}
